import { createSelector } from '@reduxjs/toolkit';
import { formValueSelector } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { getWorkflowSchemaLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/form';
import { generateJsonSchemaConfig, getActualStepObjects } from 'dashboard/lib/workflows';
import { WorkflowValidationContext } from 'dashboard/lib/workflows/validation/validationContext';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { getWorkflowActionMeta } from 'dashboard/redux/selectors/workflow-action-meta';
import {
  getInputStep,
  getPlatformObjectJsonSchema,
  getStepContexts,
  getStepsToProcessCallback,
  getUsableObjectsByKeyCallback,
  getUsableObjectsCallback,
} from 'dashboard/redux/selectors/workflow-editor-context';
import { RootState } from 'dashboard/redux/store';
import { buildGraph, findPath } from 'lib/workflows';

const getWorkflowVersionId = (_: RootState, workflowVersionId: string) => workflowVersionId;
const getActiveStepToken = (_: RootState, _workflowVersionId: string, activeStepToken?: string) =>
  activeStepToken;
const getVersionsMap = (state: RootState): Record<string, WorkflowVersion> =>
  formValueSelector(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state, 'workflowVersions');
const getFeatureFlags = (state: RootState) => state.featureFlags.featureFlags;
const getIsWorkflowErrorVariablesEnabled = (state: RootState) =>
  state.featureFlags?.featureFlags?.['global:workflow-error-variables'] || false;
export const getIsPlatformObjectJsonSchemaOnFLEEnabled = (state: RootState) =>
  state.featureFlags?.featureFlags?.['dashboard:workflow-use-platform-object-json-schema-on-fle'] ||
  false;

const getStepsToProcess = createSelector(
  [getVersionsMap, getWorkflowVersionId, getActiveStepToken],
  (versionsMap, workflowVersionId, activeStepToken) => {
    if (!versionsMap || !versionsMap[workflowVersionId]) {
      return [];
    }

    const stepsConfig = versionsMap[workflowVersionId].attributes['steps-config'];
    return getStepsToProcessCallback(stepsConfig, activeStepToken);
  },
);

export const getUsableObjectsByKey = createSelector(
  [getStepsToProcess, getInputStep, getStepContexts],
  getUsableObjectsByKeyCallback,
);

export const getUsableObjectsFLE = createSelector(
  [
    getUsableObjectsByKey,
    getActiveStepToken,
    getInputStep,
    getStepsToProcess,
    getWorkflowSchemaLiveUpdated,
    getPlatformObjectJsonSchema,
    getIsWorkflowErrorVariablesEnabled,
  ],
  getUsableObjectsCallback,
);

export const getAllWorkflowValidationContexts = createSelector(
  [
    getWorkflowSchemaLiveUpdated,
    getStepContexts,
    getInputStep,
    getVersionsMap,
    getWorkflowActionMeta,
    getFeatureFlags,
    getPlatformObjectJsonSchema,
  ],
  (
    workflowSchema,
    stepContexts,
    inputStep,
    versionsMap,
    actionMeta,
    featureFlags,
    platformObjectJsonSchema,
  ): Record<string, WorkflowValidationContext> => {
    if (!versionsMap) {
      return {};
    }

    return Object.entries(versionsMap).reduce((combined, [id, version]) => {
      const stepsConfig = version.attributes['steps-config'];

      const getContextForStep = (activeStepToken: string) => {
        const stepsToProcess = findPath(activeStepToken, stepsConfig) || [];
        const usableObjectsByKey = getUsableObjectsByKeyCallback(
          stepsToProcess,
          inputStep,
          stepContexts,
        );
        const usableObjects = getActualStepObjects({
          usableObjectsByKey,
          activeStepToken,
        });
        const { jsonSchema } = generateJsonSchemaConfig({
          usableObjects,
          useOriginal: true,
        });
        return {
          usableObjects,
          jsonSchema,
        };
      };

      const validationContext: WorkflowValidationContext = {
        graph: buildGraph(stepsConfig),
        stepsConfig,
        inputStep,
        workflowSchema,
        actionMeta,
        featureFlags,
        platformObjectJsonSchema,
        getContextForStep,
      };

      return {
        ...combined,
        [id]: validationContext,
      };
    }, {});
  },
);
